import * as THREE from 'three/webgpu';
import { LOOKS, LOOK_LABEL, type LookName } from '../lib/lut_looks.js';
import { setSnowiness } from '../lib/wetness.js';
/**
 * AtlasGraphicsMenuModule — the Esc graphics / options menu.
 *
 * A full-screen overlay (toggle: Esc) in four tabs — General (who and where you are, what draws the
 * picture), Graphics, Sound, Controls (keyboard beside gamepad).
 *
 * GRAPHICS CHANGES WAIT FOR "APPLY CHANGES" (2026-09-20). Every graphics change is a post-pipeline
 * rebuild plus an `atlas:quality-changed` (warm assets evicted, sky LUTs re-baked); the old menu paid
 * that per click, and ~35 flag rows announced nothing at all, so a toggle "did nothing" until some
 * other row rebuilt. The Graphics tab edits a DRAFT (lib/quality_tiers `inDraft`): rows show what is
 * staged and what it "was", a staged preset previews its own values, and ONE button applies the lot.
 * Sound and Controls change as they are set (a level is judged by ear; a binding is one deliberate
 * act). A live FPS / frame-time readout shows the impact so a weak machine can be dialled in.
 *
 * It drives the shared graphics-settings model in `lib/quality_tiers.ts` (the per-
 * feature setters mutate the live `activePreset()`), then calls `PostModule` to
 * rebuild the post pipeline + re-emit `atlas:quality-changed` — which Lighting /
 * Culling / Streaming / Weather / Atmosphere already react to. No new plumbing: the
 * menu is a UI over the quality system that already scales the whole pipeline.
 */
import type { App, IModule } from 'enginex';
import { makeSystem, Phase } from '../engine-compat.js';
import { versonaTts } from '../lib/tts/versona_tts.js';
import { audioRuntime } from '../lib/audio/runtime.js';
import { OUTPUT_MODES, VOLUME_GROUPS, VOLUME_LABEL, type OutputMode } from '../lib/audio/buses.js';
import {
  TIER_ORDER, TIER_LABEL, activeTier, isCustom, activePreset, setRenderScale, setAA, setShadows, setBloom, setSSAO, setDoF, setClouds, setModelSmoothing, setSurfaceDetail, setParticleDensity, setDrawDistance, setDistantDetail, distantDetailOn, MIN_DRAW_DISTANCE_M, MIN_RENDER_SCALE, RESOLUTION_TARGETS, setTargetHeight, setPlacementLod, setSsgi, setLensFlares, setLook, setSsr, setGodRays, setContactShadows, setVolumetricFogPass, setFoliageShading, setFoliageWind, currentFoliageWind, setMaterialFx, setInteriorMapping, setVfxShaders, setLitDecals, setLampCookies, setWaterFoam, setFluidPhysics, setWaterCaustics, setCloudShadows, setGroundAntiTiling, setRainRipples, setAurora, setNebulae, setPlanetAtmosphere, setUnderwaterShafts, setDqSkinning, setBloomMode, currentBloomMode, setSettlementBuildLod, setRiverWater, setGroundQuality, setSkyQuality, currentAA, currentShadows, setVignette, setSharpen, setSaturation, setMotionBlur, setFilmGrain, setChromaticAberration, setCapsuleShadows, setAerialPerspective, setAutoExposure, setBokehStyle, setLowClouds, setCloudParallax, setAAQuality, setFogDistance, setVolFogDistance, setBloomStrength, setMotionBlurIntensity, setAutoExposureStrength, setDofFocusDistance, setDofFocalLength, setDofBokehScale, currentVignette, currentSharpen, currentSaturation, currentVolumetricFogPass, currentMotionBlur, currentFilmGrain, currentChromaticAberration, currentCapsuleShadows, currentAerialPerspective, currentAutoExposure, currentBokehStyle, currentLowClouds, currentCloudParallax, currentAAQuality, currentFogDistance, currentVolFogDistance, currentDrawDistance, currentBloomStrength, currentMotionBlurIntensity, currentAutoExposureStrength, currentDofFocusDistance, currentDofFocalLength, currentDofBokehScale, setFoliageDensity, setLodDistanceScale, setSettlementDetail, setMaxTextureRes, setAnisotropy, setTonemapExposure, setTonemapOperator, setCloudMarchQuality, setWaterReflections, setColorblindMode, setColorblindStrength, setCameraFov, setAutoTune, setBloomThreshold, setBloomRadius, setAoIntensity, setAoRadius, setVignetteStrength, setFilmGrainIntensity, setChromaticStrength, setMotionBlurSamples, type AAMode, type ShadowLevel, setUpscale, setAdaptive, setUpscaleMode, setCloudResolution, type UpscaleMode, effectivePixelRatio,
  inDraft, inLive, draftChanges, commitDraft, discardDraft, setActiveTier,
  setSpecularAA, setProceduralFiltering, setVolumetricLights, setLitSmoke, setAreaLights, setGlassTransmission, setHardSurfaceAnisotropy, setRetroreflection, setAutoExposureMetering, currentAutoExposureMetering, setImpostorBlend, setRainShelter,
} from '../lib/quality_tiers.js';
import type { PostModule } from './PostModule.js';
import { bus } from '../bus.js';
import { atlas } from '../state.js';
import {
  padEnabled, setPadEnabled, padSnap, padEverSeen, padBindingText, padBindings, setPadBinding,
  resetPadBindings, padBeginCapture, padCancelCapture, padCaptureActive, padCaptureSeq,
  padLastCapture, padRumble, padRumbleEnabled, setPadRumble, padRumbleGain,
  setPadRumbleGain, padHasActuator, PAD_CONTEXTS, PAD_BUTTON_LABEL, type PadActionName,
} from '../lib/gamepad.js';
import {
  keyBindings, setKeyBinding, resetKeyBindings, keyBeginCapture, keyCancelCapture,
  keyCaptureActive, keyCaptureSeq, keyLastCapture, keyLabel, KEY_CONTEXTS,
} from '../lib/keybinds.js';
import { TOUCH_LABEL } from '../lib/touch_layout.js';

export class AtlasGraphicsMenuModule implements IModule {
  readonly name = 'GraphicsMenu';
  private app!: App;
  private overlay: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;
  private tabsEl: HTMLDivElement | null = null;
  private railEl: HTMLElement | null = null;
  private footEl: HTMLDivElement | null = null;
  private fpsEl: HTMLDivElement | null = null;
  private open = false;
  private tab: Tab = 'general';
  private renderedTab: Tab | null = null;
  /** The Graphics tab's groups (rail link + heading) and how many rows differ from the live settings. */
  private groups: Array<{ head: HTMLElement; link: HTMLElement; changed: number }> = [];
  private changedRows = 0;
  private appliedUntil = 0;
  /** `data-k` of the control last used: a re-render hands the focus back to it. */
  private focusKey = '';
  /** General tab values refreshed in place (position, frame rate). */
  private liveEls: Record<string, HTMLElement> = {};
  private fpsText = '';
  private bindSeq = 0;
  private bindActive = '';
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  // FPS moving average over ~0.5 s windows.
  private fpsAccumMs = 0;
  private fpsFrames = 0;

  init(app: App): void {
    this.app = app;
    // THE TOUR'S RAW SETTERS (2026-09-18, the shader tour): every switchable shader by ONE key, applied WITHOUT the
    // per-setter apply — `set` / `setMany` below apply once (the post rebuild is coalesced per frame anyway; this keeps
    // the quality-changed bus quiet across a 50-flag leg). lib/shader_tour.ts names these keys.
    const RAW: Record<string, (v: unknown) => void> = {
      bloom: (v) => setBloom(!!v), ao: (v) => setSSAO(!!v), dof: (v) => setDoF(!!v), motionBlur: (v) => setMotionBlur(!!v), vignette: (v) => setVignette(!!v),
      sharpen: (v) => setSharpen(typeof v === 'number' ? v : v ? 0.5 : 0), film: (v) => setFilmGrain(!!v), chromaticAberration: (v) => setChromaticAberration(!!v), capsuleShadows: (v) => setCapsuleShadows(!!v),
      ssr: (v) => setSsr(!!v), contactShadows: (v) => setContactShadows(!!v), ssgi: (v) => setSsgi(!!v), lensFlares: (v) => setLensFlares(!!v), godRays: (v) => setGodRays(!!v), volumetricFog: (v) => setVolumetricFogPass(!!v),
      aa: (v) => setAA((v === 'smaa' || v === 'fxaa' || v === 'traa' ? v : 'off') as Parameters<typeof setAA>[0]), bloomMode: (v) => setBloomMode(v === 'kawase' ? 'kawase' : 'mip'), look: (v) => setLook((typeof v === 'string' ? v : 'none') as LookName),
      autoExposure: (v) => setAutoExposure(!!v), autoExposureMetering: (v) => setAutoExposureMetering(v === 'histogram' ? 'histogram' : 'average'), aerialPerspective: (v) => setAerialPerspective(!!v),
      colorblind: (v) => setColorblindMode((typeof v === 'string' ? v : 'off') as Parameters<typeof setColorblindMode>[0]),
      clouds: (v) => setClouds(!!v), lowClouds: (v) => setLowClouds(!!v), cloudShadows: (v) => setCloudShadows(!!v), volumetricLights: (v) => setVolumetricLights(!!v), areaLights: (v) => setAreaLights(!!v), lampCookies: (v) => setLampCookies(!!v), distantDetail: (v) => setDistantDetail(!!v),
      foliageShading: (v) => setFoliageShading(!!v), foliageWind: (v) => setFoliageWind(!!v), materialFx: (v) => setMaterialFx(!!v), interiorMapping: (v) => setInteriorMapping(!!v), vfxShaders: (v) => setVfxShaders(!!v), litDecals: (v) => setLitDecals(!!v),
      specularAA: (v) => setSpecularAA(!!v), proceduralFiltering: (v) => setProceduralFiltering(!!v), glassTransmission: (v) => setGlassTransmission(!!v), hardSurfaceAnisotropy: (v) => setHardSurfaceAnisotropy(!!v), retroreflection: (v) => setRetroreflection(!!v), litSmoke: (v) => setLitSmoke(!!v),
      dqSkinning: (v) => setDqSkinning(!!v), groundAntiTiling: (v) => setGroundAntiTiling(!!v), rainRipples: (v) => setRainRipples(!!v),
      waterReflections: (v) => setWaterReflections(!!v), waterFoam: (v) => setWaterFoam(!!v), waterCaustics: (v) => setWaterCaustics(!!v), fluidPhysics: (v) => setFluidPhysics(!!v), underwaterShafts: (v) => setUnderwaterShafts(!!v),
      aurora: (v) => setAurora(!!v), nebulae: (v) => setNebulae(!!v), planetAtmosphere: (v) => setPlanetAtmosphere(!!v), upscaleMode: (v) => setUpscaleMode(v === 'taau' ? 'taau' : 'fsr'),
    };
    (window as unknown as { __gfx?: unknown }).__gfx = {
      // Probes open the menu through the module (Esc yields to any `.show` panel — the load screen, the traveler screen).
      openMenu: (tab?: string): boolean => { const t = TABS.find(([id]) => id === tab)?.[0]; if (!this.open) this.toggle(t); else if (t) this.setTab(t); return this.open; },
      closeMenu: (): boolean => { if (this.open) this.toggle(); return this.open; },
      // The menu's own state (probes): the open tab, what the Graphics draft would change, how many rows show it.
      menu: () => ({ open: this.open, tab: this.tab, pending: draftChanges(), changedRows: this.changedRows }),
      menuApply: (): boolean => { const had = draftChanges().length > 0; this.applyDraft(); return had; },
      menuDiscard: (): void => this.discard(),
      tier: () => activeTier(),
      set: (key: string, value: unknown): boolean => { const fn = RAW[key]; if (!fn) return false; fn(value); this.apply(); return true; },
      setMany: (entries: Array<[string, unknown]>): number => { let n = 0; for (const [k, v] of entries) { const fn = RAW[k]; if (fn) { fn(v); n++; } } this.apply(); return n; },
      rawKeys: () => Object.keys(RAW),
      motionBlur: (on: boolean) => { setMotionBlur(on); this.apply(); }, // probes: flip the pass (+ rebuild) without the menu
      clouds: () => activePreset().cloudsEnabled,
      setClouds: (on: boolean) => { setClouds(on); this.apply(); }, // probes: the volumetric-clouds toggle (+ rebuild)
      farCull: () => activePreset().farCull,
      chunkRadius: () => activePreset().chunkRadius,
      settlementScale: () => activePreset().settlementRadiusScale,
      fogDistance: () => currentFogDistance(),
      drawDistance: () => currentDrawDistance(),
      dofFocus: () => currentDofFocusDistance(),
      setTier: (t: string) => this.postMod()?.setTier(t as never),
      setDrawDistance: (m: number) => { setDrawDistance(m); this.apply(); },
      distantDetail: () => distantDetailOn(),
      preset: () => ({ ...activePreset() }),
      setResolution: (h: number) => { setTargetHeight(h); this.apply(); },
      // Shader wave levers (probes flip every pass without the menu):
      setSsgi: (on: boolean) => { setSsgi(on); this.apply(); },
      setLensFlares: (on: boolean) => { setLensFlares(on); this.apply(); },
      setLook: (l: string) => { setLook(l as LookName); this.apply(); },
      setSsr: (on: boolean) => { setSsr(on); this.apply(); },
      ao: () => !!activePreset().post.ao,
      setAo: (on: boolean) => { setSSAO(on); this.apply(); }, // probes: the GTAO pass on/off (+ rebuild) — the AO overlay probe
      setGodRays: (on: boolean) => { setGodRays(on); this.apply(); },
      setContactShadows: (on: boolean) => { setContactShadows(on); this.apply(); },
      setVolumetricFog: (on: boolean) => { setVolumetricFogPass(on); this.apply(); },
      passes: () => Object.keys(activePreset().post),
      setFoliageShading: (on: boolean) => { setFoliageShading(on); this.apply(); },
      setFoliageWind: (on: boolean) => { setFoliageWind(on); this.apply(); },
      foliageWind: () => currentFoliageWind(),
      setFluidPhysics: (on: boolean) => { setFluidPhysics(on); this.render(); }, // live: the water module reads the flag every tick
      fluidPhysics: () => activePreset().fluidPhysics !== false,
      setMaterialFx: (on: boolean) => { setMaterialFx(on); this.apply(); },
      // Shader wave stage 3 levers (each usable on any tier).
      setInteriorMapping: (on: boolean) => { setInteriorMapping(on); this.apply(); },
      setVfxShaders: (on: boolean) => { setVfxShaders(on); this.apply(); },
      setLitDecals: (on: boolean) => { setLitDecals(on); this.apply(); },
      setLampCookies: (on: boolean) => { setLampCookies(on); this.apply(); },
      setWaterFoam: (on: boolean) => { setWaterFoam(on); this.apply(); },
      setWaterCaustics: (on: boolean) => { setWaterCaustics(on); this.apply(); },
      // Shader wave stage 4 levers.
      setCloudShadows: (on: boolean) => { setCloudShadows(on); this.apply(); },
      setGroundAntiTiling: (on: boolean) => { setGroundAntiTiling(on); this.apply(); },
      setRainRipples: (on: boolean) => { setRainRipples(on); this.apply(); },
      setAurora: (on: boolean) => { setAurora(on); this.apply(); },
      setNebulae: (on: boolean) => { setNebulae(on); this.apply(); },
      setPlanetAtmosphere: (on: boolean) => { setPlanetAtmosphere(on); this.apply(); },
      setUnderwaterShafts: (on: boolean) => { setUnderwaterShafts(on); this.apply(); },
      setDqSkinning: (on: boolean) => { setDqSkinning(on); this.apply(); },
      // Shader additions wave (2026-09-15).
      setSpecularAA: (on: boolean) => { setSpecularAA(on); this.render(); },
      setProceduralFiltering: (on: boolean) => { setProceduralFiltering(on); this.render(); },
      setVolumetricLights: (on: boolean) => { setVolumetricLights(on); this.render(); },
      setLitSmoke: (on: boolean) => { setLitSmoke(on); this.render(); },
      setAreaLights: (on: boolean) => { setAreaLights(on); this.render(); },
      setGlassTransmission: (on: boolean) => { setGlassTransmission(on); this.render(); },
      setHardSurfaceAnisotropy: (on: boolean) => { setHardSurfaceAnisotropy(on); this.render(); },
      setRetroreflection: (on: boolean) => { setRetroreflection(on); this.render(); },
      setAutoExposureMetering: (m: 'average' | 'histogram') => { setAutoExposureMetering(m); this.apply(); },
      setBloomMode: (m: 'mip' | 'kawase') => { setBloomMode(m); this.apply(); },
      setFoliageDensity: (v: number) => { setFoliageDensity(v); this.apply(); }, // probes: the density slider without the menu
      snow: (v: number | null) => { const w = this.app?.getModule('Weather') as { setDebugSnow?: (v: number | null) => void } | undefined; if (w?.setDebugSnow) w.setDebugSnow(v); else if (v !== null) setSnowiness(v); },
      // The canvas as the renderer sees it: the applied pixel ratio + the drawing buffer (probes).
      drawingBuffer: () => { const n = this.app?.renderer.native; if (!n) return null; const v = n.getDrawingBufferSize(new THREE.Vector2()); return { pixelRatio: n.getPixelRatio(), w: v.x, h: v.y, want: effectivePixelRatio() }; },
      sceneFogDensity: () => ((this.app?.world.scene as unknown as { fog?: { density?: number } } | undefined)?.fog?.density ?? null),
      setDistantDetail: (on: boolean) => { setDistantDetail(on); this.apply(); },
      setDof: (on: boolean) => { setDoF(on); this.apply(); },
      setDofFocus: (m: number) => { setDofFocusDistance(m); this.apply(); },
    };
    this.buildOverlay();
    // The gamepad's Start button opens and closes this menu. It arrives as a bus
    // event rather than a synthetic Escape: this codebase never fakes a
    // KeyboardEvent, and a fake one would also have to re-run the arbitration
    // below (charts own Escape while they are up).
    bus.on('atlas:toggle-options', () => {
      if (document.getElementById('space-map') || document.getElementById('galaxy-map')) return;
      this.toggle();
    });
    this.keyHandler = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.repeat) return;
      if (document.getElementById('space-map') || document.getElementById('galaxy-map')) return; // an open chart owns Escape (key-collision audit 2026-08-24)
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // This runs in the CAPTURE phase — BEFORE the other panels' (bubble-phase) Esc
      // handlers — so it arbitrates which one Esc belongs to:
      if (this.open) {
        // My menu is open → close it and consume Esc so no panel also reacts.
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
        return;
      }
      // This Esc is releasing the canvas pointer-lock (the mouse was captured for mouse-
      // look) — let the browser do that and DON'T pop the menu on the same press. The next
      // Esc (mouse now free) opens it: the natural "Esc to free the mouse, Esc again for the
      // menu" flow.
      if (document.pointerLockElement) return;
      // Yield to an open MODAL panel (panels mark themselves `.show` when open) so its own
      // Esc closes it first. The always-on HUDs (#wallet, #pulse) are ALSO `.show`
      // PERMANENTLY — exclude them, or this guard is always true and the menu can never open
      // in-game. (Any future always-on `.show` HUD must be excluded here too.)
      if (document.querySelector('.show:not(#wallet):not(#pulse)')) return;
      e.preventDefault();
      this.toggle();
    };
    window.addEventListener('keydown', this.keyHandler, true); // capture: arbitrate before panel handlers
    app.world.addSystem(makeSystem('GraphicsMenuFps', Phase.UI, 91, (dt) => this.updateFps(dt)));
    // A rebind resolves inside the pad's Input-phase tick, so the panel watches
    // for it here rather than polling the controller a second time.
    app.world.addSystem(makeSystem('GraphicsMenuRebind', Phase.UI, 92, () => {
      if (!this.open) return;
      const seq = padCaptureSeq() + keyCaptureSeq() * 1000;
      const active = `${padCaptureActive() ?? ''}|${keyCaptureActive() ?? ''}`;
      if (seq !== this.bindSeq || active !== this.bindActive) { this.bindSeq = seq; this.bindActive = active; this.render(); }
    }));
    window.setTimeout(() => this.applyFov(), 1500); // persisted FOV once the camera exists
  }

  private postMod(): PostModule | undefined { return this.app.getModule<PostModule>('Post'); }
  /** An IMMEDIATE change — the probe levers on `__gfx`. The menu's own Graphics rows go through the draft (`stage`). */
  private apply(): void { this.postMod()?.applySettings(); this.render(); }
  /** Stage a graphics change: it edits the DRAFT (lib/quality_tiers) and waits for "Apply changes". */
  private stage(fn: () => void): void { inDraft(fn); this.render(); }
  /**
   * "Apply changes": the draft becomes the live settings — ONE pipeline rebuild and ONE `atlas:quality-changed` for
   * any number of rows. (Before 2026-09-20 every row rebuilt on its own click, and the ~35 flag rows announced
   * nothing at all: a toggle "did nothing" until some other row happened to rebuild.)
   */
  private applyDraft(): void {
    if (!commitDraft()) { this.render(); return; }
    this.postMod()?.applySettings();
    this.applyFov();
    (this.app.getModule('Clouds') as { setParallaxScale?(x: number): void } | undefined)?.setParallaxScale?.(currentCloudParallax());
    this.appliedUntil = performance.now() + 2600;
    this.render();
    window.setTimeout(() => { if (this.open) this.render(); }, 2700);
  }
  private discard(): void { discardDraft(); this.appliedUntil = 0; this.render(); }

  private setTab(tab: Tab): void {
    this.tab = tab;
    try { localStorage.setItem('atlas:options-tab', tab); } catch { /* storage denied */ }
    this.render();
  }

  private toggle(tab?: Tab): void {
    this.open = !this.open;
    if (this.open && tab) this.tab = tab;
    if (this.overlay) this.overlay.style.display = this.open ? 'flex' : 'none';
    if (this.open) this.render();
  }

  private buildOverlay(): void {
    if (!document.getElementById('atlas-options-style')) {
      const st = document.createElement('style');
      st.id = 'atlas-options-style';
      st.textContent = OPTIONS_CSS;
      document.head.appendChild(st);
    }
    const overlay = document.createElement('div');
    overlay.id = 'atlas-options';
    overlay.dataset.atlasNav = 'options';   // the gamepad's UI navigator finds it here
    const panel = el('div', 'op-panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Options');

    const head = el('div', 'op-head');
    head.appendChild(el('div', 'op-title', 'Options'));
    this.fpsEl = el('div', 'op-fps');
    this.fpsEl.append(el('span', '', '– fps'), el('span', '', '– ms'));
    const close = btn('Close', () => this.toggle(), 'op-btn op-close');
    close.dataset.atlasClose = '1';        // the gamepad's B closes through this
    close.title = 'Close the menu (Esc)';
    head.append(this.fpsEl, close);

    this.tabsEl = el('div', 'op-tabs');
    this.tabsEl.setAttribute('role', 'tablist');
    const main = el('div', 'op-main');
    this.railEl = el('nav', 'op-rail');
    this.railEl.setAttribute('aria-label', 'Graphics groups');
    this.body = el('div', 'op-body');
    this.body.addEventListener('scroll', () => this.spy(), { passive: true });
    main.append(this.railEl, this.body);
    this.footEl = el('div', 'op-apply');
    this.footEl.setAttribute('aria-live', 'polite');
    panel.append(head, this.tabsEl, main, this.footEl);
    overlay.appendChild(panel);
    // Click the dimmed backdrop (outside the panel) closes the menu.
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.toggle(); });
    // Remember WHICH control was used, so the re-render that follows a click hands the focus back to it
    // (keyboard and gamepad alike — a pad player was thrown to the top of the list after every change).
    overlay.addEventListener('click', (e) => { const k = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-k]')?.dataset.k; if (k) this.focusKey = k; }, true);
    document.body.appendChild(overlay);
    this.overlay = overlay;
    try { const t = localStorage.getItem('atlas:options-tab'); if (t && TABS.some(([id]) => id === t)) this.tab = t as Tab; } catch { /* storage denied */ }
  }

  /** Re-render the open tab from the current settings. */
  private render(): void {
    if (!this.body || !this.tabsEl || !this.railEl || !this.footEl) return;
    const keepScroll = this.renderedTab === this.tab ? this.body.scrollTop : 0;
    const pending = draftChanges();
    this.tabsEl.replaceChildren(...TABS.map(([id, label]) => {
      const b = btn(label, () => this.setTab(id), 'op-tab');
      b.dataset.tab = id;                  // the pad's shoulders walk these
      b.dataset.k = `tab:${id}`;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', String(id === this.tab));
      if (id === 'graphics' && pending.length) { b.dataset.dirty = '1'; b.title = 'Graphics changes are waiting to be applied'; }
      return b;
    }));
    this.body.replaceChildren();
    this.railEl.replaceChildren();
    this.groups = [];
    this.changedRows = 0;
    this.liveEls = {};
    this.railEl.style.display = this.tab === 'graphics' ? '' : 'none';
    if (this.tab === 'graphics') inDraft(() => this.renderGraphics());
    else if (this.tab === 'sound') this.renderSound();
    else if (this.tab === 'controls') this.renderControls();
    else this.renderGeneral();
    this.renderFoot(pending);
    this.body.scrollTop = keepScroll;
    this.renderedTab = this.tab;
    this.spy();
    if (this.focusKey) {
      const again = this.overlay?.querySelector<HTMLElement>(`[data-k="${CSS.escape(this.focusKey)}"]`);
      if (again && document.activeElement !== again) { try { again.focus({ preventScroll: true }); } catch { /* not focusable */ } }
    }
  }

  // ── The Apply bar ────────────────────────────────────────────────────────────
  private renderFoot(pending: string[]): void {
    const foot = this.footEl!;
    foot.replaceChildren();
    foot.style.display = this.tab === 'graphics' ? '' : 'none';
    if (this.tab !== 'graphics') return;
    const has = pending.length > 0;
    foot.classList.toggle('is-pending', has);
    const stagedTier = has && pending.includes('preset') ? inDraft(() => TIER_LABEL[activeTier()]) : null;
    const n = Math.max(1, this.changedRows);
    const text = has
      ? (stagedTier ? `${stagedTier} preset waiting to be applied` : `${n} ${n === 1 ? 'change' : 'changes'} waiting to be applied`)
      : performance.now() < this.appliedUntil ? 'Changes applied' : 'No changes to apply';
    foot.appendChild(el('div', 'op-apply-text', text));
    const discard = btn('Discard', () => this.discard(), 'op-btn op-apply-discard');
    const go = btn('Apply changes', () => this.applyDraft(), 'op-btn op-apply-go');
    discard.dataset.k = 'foot:discard'; go.dataset.k = 'foot:apply';
    discard.disabled = !has; go.disabled = !has;
    foot.append(discard, go);
  }

  /** The rail follows the scroll: the group whose heading last passed the top is "here". */
  private spy(): void {
    if (this.tab !== 'graphics' || !this.body || !this.groups.length) return;
    const top = this.body.scrollTop + 24;
    let here = this.groups[0];
    for (const g of this.groups) if (g.head.offsetTop <= top) here = g;
    if (this.body.scrollTop + this.body.clientHeight >= this.body.scrollHeight - 2) here = this.groups[this.groups.length - 1];
    for (const g of this.groups) g.link.classList.toggle('is-here', g === here);
  }

  // ── Graphics rows: each reads its value TWICE — from the draft (what is shown) and from the live settings (what
  //    "was") — so a row that would change on Apply says so. `when` = an honest note for settings that still wait
  //    for something after Apply (a reload, the next world). ──
  private group(title: string): HTMLDivElement {
    const head = el('div', 'op-group', title);
    const link = btn(title, () => { if (this.body) this.body.scrollTop = head.offsetTop - 6; }, 'op-rail-link');
    link.dataset.k = `rail:${title}`;
    this.body!.appendChild(head);
    this.railEl!.appendChild(link);
    const g = { head, link, changed: 0 };
    this.groups.push(g);
    return head;
  }
  private addRow(row: HTMLElement, changed: boolean): void {
    if (changed) {
      row.classList.add('is-changed');
      this.changedRows++;
      const g = this.groups[this.groups.length - 1];
      if (g) { g.changed++; g.link.dataset.dirty = '1'; }
    }
    this.body!.appendChild(row);
  }
  private gSeg(label: string, values: readonly string[], labels: readonly string[], get: () => string, set: (v: string) => void, when = ''): string {
    const cur = get(), was = inLive(get);
    const wasLabel = labels[values.indexOf(was)] ?? was;
    this.addRow(segRow(label, values, labels, cur, (v) => this.stage(() => set(v)), when, cur !== was ? `was ${wasLabel}` : ''), cur !== was);
    return cur;
  }
  private gToggle(label: string, get: () => boolean, set: (on: boolean) => void, when = ''): boolean {
    return this.gSeg(label, ['off', 'on'], ['Off', 'On'], () => (get() ? 'on' : 'off'), (v) => set(v === 'on'), when) === 'on';
  }
  private gSlider(label: string, min: number, max: number, step: number, get: () => number, fmt: (v: number) => string, set: (v: number) => void, when = ''): number {
    const cur = get(), was = inLive(get);
    const changed = Math.abs(cur - was) > 1e-9;
    this.addRow(sliderRow(label, min, max, step, cur, fmt, (v) => this.stage(() => set(v)), false, when, changed ? `was ${fmt(was)}` : ''), changed);
    return cur;
  }

  private renderGraphics(): void {
    const P = (): ReturnType<typeof activePreset> => activePreset(); // the DRAFT while rendering; the live settings inside inLive()

    // ── Preset ──
    this.group('Preset');
    {
      const cur = `${activeTier()}|${isCustom() ? 'c' : ''}`, was = inLive(() => `${activeTier()}|${isCustom() ? 'c' : ''}`);
      const row = el('div', 'op-row op-row-wide');
      const seg = el('div', 'op-seg');
      for (const t of TIER_ORDER) {
        const b = btn(TIER_LABEL[t], () => this.stage(() => setActiveTier(t)));
        b.dataset.k = `Preset|${t}`;
        if (activeTier() === t) b.classList.add(isCustom() ? 'base' : 'on'); // customised: the preset it started from is underlined, not filled
        seg.appendChild(b);
      }
      row.appendChild(seg);
      const note = isCustom() ? `${TIER_LABEL[activeTier()]}, customised` : '';
      if (note) row.appendChild(el('span', 'op-when', note));
      const tierChanged = cur.split('|')[0] !== was.split('|')[0];
      if (tierChanged) row.appendChild(el('span', 'op-was', `was ${inLive(() => TIER_LABEL[activeTier()])}`));
      this.addRow(row, tierChanged);
      this.body!.appendChild(noteRow('A preset sets every row below. Change any row afterwards and the preset becomes customised.'));
    }
    this.gToggle('Auto-tune', () => P().autoTune ?? false, setAutoTune, 'moves the preset with your frame rate');

    // ── Resolution ──
    this.group('Resolution');
    // Resolution TARGET (2026-09-08): the canvas height whatever the display — minimal 480p … ultra 4K; Native = the ratio slider below.
    this.gSeg('Resolution', RESOLUTION_TARGETS.map((t) => String(t.height)), RESOLUTION_TARGETS.map((t) => t.label), () => String(P().targetHeight ?? 0), (v) => setTargetHeight(Number(v)));
    this.gSlider('Render scale', MIN_RENDER_SCALE, 2, 0.05, () => P().pixelRatio, (v) => `${v.toFixed(2)}×`, setRenderScale);
    this.gSeg('Upscaling', ['off', 'ultra', 'quality', 'balanced', 'performance'], ['Off', 'Ultra 1.3×', 'Quality 1.5×', 'Balanced 1.7×', 'Perf 2×'], () => P().upscale ?? 'off', (v) => setUpscale(v as UpscaleMode));
    this.gSeg('Upscaler', ['fsr', 'taau'], ['FSR 1, spatial', 'TAAU, temporal'], () => P().upscaleMode ?? 'fsr', (v) => setUpscaleMode(v as 'fsr' | 'taau'));
    this.gToggle('Adaptive render scale', () => P().adaptive ?? false, setAdaptive);
    const aa = this.gSeg('Anti-aliasing', ['off', 'fxaa', 'smaa', 'traa'], ['Off', 'FXAA', 'SMAA', 'TRAA'], () => currentAA(), (m) => setAA(m as AAMode));
    if (aa !== 'off') this.gSeg('Anti-aliasing quality', ['2', '4', '8', '16', '32'], ['2×', '4×', '8×', '16×', '32×'], () => String(currentAAQuality()), (v) => setAAQuality(Number(v)));
    this.gSeg('Texture quality', ['512', '1024', '2048', '4096'], ['¼', '½', 'Full', 'Max'], () => String(P().maxTextureRes), (v) => setMaxTextureRes(Number(v)));
    this.gSeg('Anisotropy', ['1', '2', '4', '8', '16'], ['1×', '2×', '4×', '8×', '16×'], () => String(P().anisotropy ?? 4), (v) => setAnisotropy(Number(v)));
    this.gSlider('Field of view', 50, 100, 1, () => ((P().cameraFov ?? 0) > 0 ? P().cameraFov! : this.currentFov()), (v) => `${Math.round(v)}°`, setCameraFov);

    // ── Light and shadow ──
    this.group('Light and shadow');
    this.gSeg('Shadows', ['off', 'low', 'med', 'high', 'ultra'], ['Off', 'Low', 'Med', 'High', 'Ultra'], () => currentShadows(), (m) => setShadows(m as ShadowLevel));
    this.gToggle('Capsule shadows', () => currentCapsuleShadows(), setCapsuleShadows);
    this.gToggle('Contact shadows', () => !!P().post.contactShadows, setContactShadows);
    this.gToggle('Cloud shadows', () => P().cloudShadows === true, setCloudShadows);
    if (this.gToggle('Ambient occlusion', () => !!P().post.ao, setSSAO)) {
      this.gSlider('Ambient occlusion intensity', 0, 3, 0.1, () => P().post.ao?.intensity ?? 1, (v) => v.toFixed(1), setAoIntensity);
      this.gSlider('Ambient occlusion radius', 0.1, 2, 0.05, () => P().post.ao?.radius ?? 0.5, (v) => `${v.toFixed(2)} m`, setAoRadius);
    }
    this.gToggle('Global illumination (SSGI)', () => !!P().post.ssgi, setSsgi);
    this.gToggle('Screen-space reflections', () => !!P().post.ssr, setSsr, 'WebGPU only');
    this.gToggle('Volumetric light shafts', () => P().volumetricLights === true, setVolumetricLights);
    this.gToggle('Window area lights at night', () => P().areaLights === true, setAreaLights, 'after a reload');
    this.gToggle('Lamp light cookies', () => P().lampCookies === true, setLampCookies, 'after a reload');

    // ── World detail ──
    this.group('World detail');
    this.gSlider('Draw distance', MIN_DRAW_DISTANCE_M, 5000, 100, () => currentDrawDistance(), (v) => `${(v / 1000).toFixed(1)} km`, setDrawDistance);
    // Distant detail (2026-09-08): everything past the draw distance — HLOD settlements, silhouettes, the far terrain — or a fog-closed void.
    this.gToggle('Distant detail', () => P().distantDetail !== false, setDistantDetail, 'far towns and terrain past the draw distance');
    this.gSlider('Foliage density', 0, 1.5, 0.05, () => P().foliageDensityScale, (v) => `${Math.round(v * 100)}%`, setFoliageDensity);
    this.gSlider('Level-of-detail distance', 0.5, 6, 0.25, () => P().lodDistanceScale, (v) => `${v.toFixed(2)}×`, setLodDistanceScale);
    this.gSlider('Settlement detail', 0.5, 3, 0.1, () => P().settlementRadiusScale, (v) => `${v.toFixed(1)}×`, setSettlementDetail);
    this.gSeg('Settlement build', ['1', '2'], ['Detailed', 'Simplified'], () => String(P().settlementBuildLod ?? 1), (v) => setSettlementBuildLod(Number(v) as 1 | 2));
    this.gSeg('Placement detail', ['0', '1', '2', '3'], ['Full', '½', '¼', 'Min'], () => String(P().placementLod ?? 0), (v) => setPlacementLod(Number(v) as 0 | 1 | 2 | 3));
    this.gSlider('Particle density', 0, 1, 0.05, () => P().particleCap, (v) => `${Math.round(v * 100)}%`, setParticleDensity);
    this.gToggle('Model smoothing', () => P().meshSmoothing, setModelSmoothing);
    this.gToggle('Surface detail', () => P().surfaceDetail, setSurfaceDetail);
    this.gSeg('Ground shader', ['full', 'simple'], ['Full', 'Simple'], () => P().groundQuality ?? 'full', (v) => setGroundQuality(v as 'full' | 'simple'), 'after a reload');
    this.gToggle('Ground anti-tiling', () => P().groundAntiTiling === true, setGroundAntiTiling, 'after a reload');
    this.gToggle('Far-tree frame blending', () => P().impostorBlend === true, setImpostorBlend, 'at the next tree bake');
    this.gToggle('Dual-quaternion skinning', () => P().dqSkinning === true, setDqSkinning);

    // ── Sky and weather ──
    this.group('Sky and weather');
    this.gSeg('Sky', ['full', 'flat'], ['Atmosphere', 'Flat'], () => P().skyQuality ?? 'full', (v) => setSkyQuality(v as 'full' | 'flat'), 'after a reload');
    if (this.gToggle('Volumetric clouds', () => P().cloudsEnabled, setClouds)) {
      // Second low cloud layer (~800-1500 m puffs) — only meaningful when the volumetric clouds themselves are on.
      this.gToggle('Low clouds', () => currentLowClouds(), setLowClouds);
      // 0 = frozen to the camera; 1 (default) = full world parallax — clouds stationary in the world.
      this.gSlider('Cloud parallax', 0, 1, 0.05, () => currentCloudParallax(), (v) => (v === 0 ? 'Frozen' : v.toFixed(2)), setCloudParallax);
      this.gSlider('Cloud resolution', 0.25, 1, 0.25, () => P().cloudResolution ?? 1, (v) => `${Math.round(v * 100)}%`, setCloudResolution);
      this.gSeg('Cloud march quality', ['auto', 'low', 'medium', 'high'], ['Auto', 'Low', 'Med', 'High'], () => P().cloudMarchQuality ?? 'auto', (v) => setCloudMarchQuality(v as 'auto' | 'low' | 'medium' | 'high'));
    }
    // Distance fog: toggle + distance (metres to the ~63% extinction depth).
    if (this.gToggle('Distance fog', () => currentFogDistance() > 0, (on) => setFogDistance(on ? 1200 : 0))) {
      this.gSlider('Fog distance', 400, 5000, 100, () => currentFogDistance(), (v) => `${v} m`, setFogDistance);
    }
    if (this.gToggle('Volumetric fog', () => currentVolumetricFogPass(), setVolumetricFogPass)) {
      this.gSlider('Volumetric fog distance', 60, 600, 20, () => currentVolFogDistance(), (v) => `${v} m`, setVolFogDistance);
    }
    // Aerial haze needs the sky's froxel bake, which only starts when the page boots with it on.
    this.gToggle('Aerial haze', () => currentAerialPerspective(), setAerialPerspective, 'after a reload');
    this.gToggle('Aurora nights', () => P().aurora === true, setAurora);
    this.gToggle('Rain ripples', () => P().rainRipples === true, setRainRipples, 'after a reload');
    this.gToggle('Rain shelter', () => P().rainShelter === true, setRainShelter, 'roofs stop rain and keep the ground dry');
    this.gToggle('Nebula volumes', () => P().nebulae === true, setNebulae, 'in space');
    this.gToggle('Planet atmospheres', () => P().planetAtmosphere === true, setPlanetAtmosphere, 'in space');

    // ── Water ──
    this.group('Water');
    this.gSeg('River water', ['realistic', 'simple'], ['Realistic', 'Simple'], () => P().riverWater ?? 'realistic', (v) => setRiverWater(v as 'realistic' | 'simple'));
    this.gToggle('Water reflections', () => P().waterReflections ?? false, setWaterReflections, 'at the next world');
    this.gToggle('Realistic fluid physics', () => P().fluidPhysics !== false, setFluidPhysics, 'wakes, wading, the wind on the sea');
    this.gToggle('Sea foam trails', () => P().waterFoam === true, setWaterFoam, 'after a reload');
    this.gToggle('Projected caustics', () => P().waterCaustics === true, setWaterCaustics, 'after a reload');
    this.gToggle('Underwater light shafts', () => P().underwaterShafts === true, setUnderwaterShafts, 'after a reload');

    // ── Materials ──
    this.group('Materials');
    this.gToggle('Foliage translucency', () => P().foliageShading !== false, setFoliageShading, 'leaves lit from behind');
    // Wind has its OWN switch (2026-09-14) so a custom profile on a cheap tier can still have the trees move.
    this.gToggle('Tree and foliage wind', () => currentFoliageWind(), setFoliageWind);
    this.gToggle('Material effects', () => P().materialFx !== false, setMaterialFx, 'snow, clearcoat');
    this.gToggle('Interior mapping', () => P().interiorMapping === true, setInteriorMapping, 'rooms behind windows');
    this.gToggle('Effect shaders', () => P().vfxShaders === true, setVfxShaders, 'haze, shields, dissolve, holograms');
    this.gToggle('Lit impact decals', () => P().litDecals === true, setLitDecals);
    this.gToggle('Specular anti-aliasing', () => P().specularAA === true, setSpecularAA, 'normal-map roughness filter');
    this.gToggle('Procedural detail filtering', () => P().proceduralFiltering === true, setProceduralFiltering, 'no shimmer at distance');
    this.gToggle('Lit smoke and dust', () => P().litSmoke === true, setLitSmoke);
    this.gToggle('Glass transmission', () => P().glassTransmission === true, setGlassTransmission, 'props, canopies');
    this.gToggle('Brushed-metal anisotropy', () => P().hardSurfaceAnisotropy === true, setHardSurfaceAnisotropy);
    this.gToggle('Retroreflective signs and markers', () => P().retroreflection === true, setRetroreflection);

    // ── Camera and lens ──
    this.group('Camera and lens');
    if (this.gToggle('Bloom', () => !!P().post.bloom, setBloom)) {
      this.gSeg('Bloom filter', ['mip', 'kawase'], ['Gaussian', 'Kawase, cheaper'], () => currentBloomMode(), (v) => setBloomMode(v as 'mip' | 'kawase'));
      this.gSlider('Bloom intensity', 0, 1.5, 0.05, () => currentBloomStrength(), (v) => v.toFixed(2), setBloomStrength);
      this.gSlider('Bloom threshold', 0.4, 1.6, 0.05, () => P().post.bloom?.threshold ?? 1.1, (v) => v.toFixed(2), setBloomThreshold);
      this.gSlider('Bloom radius', 0, 1, 0.05, () => P().post.bloom?.radius ?? 0.6, (v) => v.toFixed(2), setBloomRadius);
    }
    if (this.gToggle('Depth of field', () => !!P().post.dof, setDoF)) {
      this.gSeg('Bokeh style', ['standard', 'hex'], ['Standard', 'Hex'], () => currentBokehStyle(), (m) => setBokehStyle(m as 'standard' | 'hex'));
      this.gSlider('Focus distance', 2, 4000, 5, () => currentDofFocusDistance(), (v) => (v >= 1000 ? `${(v / 1000).toFixed(2)} km` : `${v} m`), setDofFocusDistance);
      // Metres of focus RANGE, not a lens length (2026-09-14) — the old 0.01–0.2 slider blurred the entire world at every position on it.
      this.gSlider('Focus range', 4, 400, 1, () => currentDofFocalLength(), (v) => `${Math.round(v)} m`, setDofFocalLength);
      this.gSlider('Bokeh scale', 0.3, 3, 0.1, () => currentDofBokehScale(), (v) => `${v.toFixed(1)}×`, setDofBokehScale);
    }
    if (this.gToggle('Motion blur', () => currentMotionBlur(), setMotionBlur)) {
      this.gSlider('Motion blur intensity', 0.25, 3, 0.25, () => currentMotionBlurIntensity(), (v) => `${v.toFixed(2)}×`, setMotionBlurIntensity);
      this.gSlider('Motion blur samples', 4, 32, 4, () => P().post.motionBlur?.samples ?? 12, (v) => String(v), setMotionBlurSamples);
    }
    this.gToggle('Lens flares', () => !!(P().post as { lensFlares?: unknown }).lensFlares, setLensFlares);
    this.gToggle('God rays', () => !!P().post.godRays, setGodRays);
    if (this.gToggle('Vignette', () => currentVignette(), setVignette)) {
      this.gSlider('Vignette strength', 0, 1, 0.05, () => P().post.vignette?.strength ?? 0.4, (v) => v.toFixed(2), setVignetteStrength);
    }
    if (this.gToggle('Film grain', () => currentFilmGrain(), setFilmGrain)) {
      this.gSlider('Film grain intensity', 0, 1, 0.05, () => P().post.film?.intensity ?? 0.35, (v) => v.toFixed(2), setFilmGrainIntensity);
    }
    if (this.gToggle('Chromatic aberration', () => currentChromaticAberration(), setChromaticAberration)) {
      this.gSlider('Chromatic aberration strength', 0, 2, 0.05, () => P().post.chromaticAberration?.strength ?? 0.6, (v) => v.toFixed(2), setChromaticStrength);
    }

    // ── Colour ──
    this.group('Colour');
    this.gSeg('Tone mapping', ['aces', 'agx', 'neutral', 'reinhard'], ['ACES', 'AgX', 'Neutral', 'Reinhard'], () => P().tonemapOperator ?? 'aces', (v) => setTonemapOperator(v as 'aces' | 'agx' | 'neutral' | 'reinhard'));
    this.gSlider('Exposure', 0.4, 1.8, 0.05, () => P().tonemapExposure ?? 0.85, (v) => v.toFixed(2), setTonemapExposure);
    if (this.gToggle('Auto exposure', () => currentAutoExposure(), setAutoExposure)) {
      this.gSlider('Auto exposure strength', 0, 1, 0.05, () => currentAutoExposureStrength(), (v) => `${Math.round(v * 100)}%`, setAutoExposureStrength);
      this.gSeg('Exposure metering', ['average', 'histogram'], ['Log average', 'Histogram, trimmed'], () => currentAutoExposureMetering(), (m) => setAutoExposureMetering(m as 'average' | 'histogram'));
    }
    this.gSeg('Look', LOOKS, LOOKS.map((l) => LOOK_LABEL[l]), () => P().look ?? 'none', (v) => setLook(v as LookName));
    this.gSlider('Saturation', 0.5, 1.5, 0.05, () => currentSaturation(), (v) => `${Math.round(v * 100)}%`, setSaturation);
    this.gSlider('Sharpen', 0, 0.6, 0.05, () => currentSharpen(), (v) => (v === 0 ? 'Off' : v.toFixed(2)), setSharpen);
    const cb = this.gSeg('Colourblind correction', ['off', 'protanopia', 'deuteranopia', 'tritanopia'], ['Off', 'Protanopia', 'Deuteranopia', 'Tritanopia'], () => P().colorblindMode ?? 'off', (v) => setColorblindMode(v as 'off' | 'protanopia' | 'deuteranopia' | 'tritanopia'));
    if (cb !== 'off') this.gSlider('Correction strength', 0, 1, 0.05, () => P().colorblindStrength ?? 1, (v) => `${Math.round(v * 100)}%`, setColorblindStrength);
  }

  // ── Sound ── (the shared audio runtime, 2026-09-20; versona voices: KittenTTS in the browser, 2026-09-10)
  // Every row writes the runtime (lib/audio/runtime.ts), which persists it and ramps the live graph — the menu
  // holds no audio state of its own. Sound changes AS YOU SET IT: a level is judged by ear, not by a button.
  private renderSound(): void {
    const tts = versonaTts();
    const rt = audioRuntime();
    const pct = (v: number): string => `${Math.round(v * 100)}%`;
    const cols = el('div', 'op-cols');
    const left = el('div'), right = el('div');
    left.appendChild(el('div', 'op-group', 'Levels'));
    left.appendChild(toggleRow('Mute all game audio', rt.isMuted(), (on) => { rt.setMuted(on); this.render(); }));
    for (const g of VOLUME_GROUPS) {
      const m = /^(.*?)\s*\((.*)\)\s*$/.exec(VOLUME_LABEL[g]);   // "Effects (combat, vehicles, …)" → a label and its note
      const name = m ? m[1] : VOLUME_LABEL[g], what = m ? m[2] : '';
      // Dialogue keeps its older control: the TTS service owns that gain (one owner — never applied twice).
      if (g === 'dialogue') left.appendChild(sliderRow(name, 0, 1, 0.05, tts.volume(), pct, (v) => { tts.setVolume(v); rt.setVolume('dialogue', v); /* same key, same value: keeps __audio() honest; the bus gain stays 1 */ }, true, what));
      else left.appendChild(sliderRow(name, 0, 1, 0.05, rt.volume(g), pct, (v) => { rt.setVolume(g, v); }, true, what));
    }
    right.appendChild(el('div', 'op-group', 'Output'));
    right.appendChild(segRow('Output', [...OUTPUT_MODES], ['Headphones', 'Speakers', 'Mono'], rt.outputMode(), (v) => { rt.setOutputMode(v as OutputMode); this.render(); }));
    right.appendChild(noteRow(rt.outputMode() === 'headphones'
      ? 'Headphones: the sounds you need to locate (threats, evidence, speech) are placed around you — above, behind, beside.'
      : rt.outputMode() === 'speakers' ? 'Speakers: plain stereo panning. Placement around the head smears over loudspeakers.'
      : 'Mono: both ears carry everything — for single-sided hearing, one earbud or a phone speaker. Turn on sound captions for direction.'));
    right.appendChild(segRow('Dynamic range', ['full', 'night'], ['Full', 'Night'], rt.isNightMode() ? 'night' : 'full', (v) => { rt.setNightMode(v === 'night'); this.render(); }));
    right.appendChild(toggleRow('Room acoustics', rt.isReverbEnabled(), (on) => { rt.setReverbEnabled(on); this.render(); }, 'caves, halls, interiors'));
    right.appendChild(toggleRow('Sound captions', rt.captionsEnabled(), (on) => { rt.setCaptionsEnabled(on); this.render(); }, 'what you heard, and which way'));
    {
      const row = labeledRow('Speaker check');
      const seg = el('div', 'op-seg');
      for (const [label, where] of [['Left', 'left'], ['Ahead', 'ahead'], ['Right', 'right']] as const) {
        const b = btn(label, () => { rt.playTest(where); });
        b.dataset.k = `Speaker check|${where}`;
        seg.appendChild(b);
      }
      row.lastElementChild!.appendChild(seg);
      right.appendChild(row);
    }
    right.appendChild(el('div', 'op-group', 'Voices'));
    right.appendChild(toggleRow('Versona voices', tts.enabled(), (on) => { tts.setEnabled(on); this.render(); }, 'spoken dialogue'));
    right.appendChild(toggleRow('Overheard chatter', tts.ambientEnabled(), (on) => {
      try { localStorage.setItem('atlas:voices-ambient', on ? 'on' : 'off'); } catch { /* storage denied */ }
      if (!on) { try { if (tts.liveRank() === 1) tts.stop(); } catch { /* not built */ } }
      this.render();
    }, 'one voice at a time, within earshot'));
    cols.append(left, right);
    this.body!.appendChild(cols);
  }

  // ── Controls ── keyboard (rebindable, 2026-09-16) beside the gamepad (gamepad everywhere, 2026-09-16)
  private renderControls(): void {
    const body = this.body!;
    const cols = el('div', 'op-cols');
    const kb = el('div'), pad = el('div');
    kb.dataset.bind = 'key'; pad.dataset.bind = 'pad';

    // ── Keyboard ──
    {
      kb.appendChild(el('div', 'op-group', 'Keyboard'));
      const head = el('div', 'op-bind-head');
      head.appendChild(el('div', 'op-note', keyCaptureActive() ? 'Press a key. Esc cancels.' : 'Bindings follow the physical key, so changing the keyboard layout never moves them.'));
      const reset = btn('Reset keyboard', () => { resetKeyBindings(); keyCancelCapture(); this.render(); }, 'op-btn op-btn-small');
      reset.dataset.k = 'key:reset';
      head.appendChild(reset);
      kb.appendChild(head);
      const live = keyBindings();
      const seenKey = new Set<string>();
      for (const [ctx, actions] of Object.entries(KEY_CONTEXTS)) {
        kb.appendChild(el('div', 'op-bind-ctx', contextLabel(ctx)));
        for (const a2 of actions) {
          if (seenKey.has(a2)) continue;
          seenKey.add(a2);
          const row = el('div', 'op-bind');
          row.dataset.action = a2;
          const capturing = keyCaptureActive() === a2;
          const set = btn(capturing ? 'Press a key' : (live[a2].length ? live[a2].map(keyLabel).join(' / ') : 'Unbound'),
            () => { if (keyCaptureActive() === a2) keyCancelCapture(); else keyBeginCapture(a2); this.render(); }, `op-btn op-btn-small op-bind-set${capturing ? ' is-capturing' : ''}`);
          set.dataset.k = `key:${a2}`;
          const clear = btn('Unbind', () => { setKeyBinding(a2, []); this.render(); }, 'op-btn op-btn-small op-bind-clear');
          clear.dataset.k = `key:${a2}:clear`;
          clear.disabled = live[a2].length === 0;
          row.append(el('div', 'op-bind-name', actionLabel(a2)), set, clear);
          kb.appendChild(row);
        }
      }
      const lastK = keyLastCapture();
      if (lastK) {
        const bad = lastK.conflicts.length > 0;
        kb.appendChild(el('div', `op-result${bad ? ' is-bad' : ''}`, bad
          ? `${actionLabel(lastK.action)} cannot take that key: ${lastK.conflicts.map(actionLabel).join(', ')} already ${lastK.conflicts.length === 1 ? 'uses' : 'use'} it in the same situation.`
          : lastK.code ? `${actionLabel(lastK.action)} is now ${keyLabel(lastK.code)}.` : `${actionLabel(lastK.action)} was left unchanged.`));
      }
    }

    // ── Gamepad ──
    {
      pad.appendChild(el('div', 'op-group', 'Gamepad'));
      pad.appendChild(toggleRow('Gamepad', padEnabled(), (on) => { setPadEnabled(on); this.render(); }));
      const snap = padSnap();
      const supported = typeof (navigator as Navigator & { getGamepads?: unknown }).getGamepads === 'function';
      pad.appendChild(noteRow(!supported
        ? 'This browser has no gamepad support. It needs a secure page (https or localhost).'
        : snap.present
          ? `Connected: ${snap.id || 'controller'}${snap.mapping === 'standard' ? '' : ' (non-standard layout, mapped by what it can do)'}`
          : padEverSeen()
            ? 'No controller right now.'
            : 'Plug in a controller and press any button on it. Browsers hide controllers until then.'));
      pad.appendChild(toggleRow('Rumble', padRumbleEnabled(), (on) => { setPadRumble(on); if (on) padRumble('hit'); this.render(); }));
      if (padRumbleEnabled() && padSnap().present && !padHasActuator()) pad.appendChild(noteRow('This controller reports no haptics. Firefox and some Bluetooth connections do not expose them.'));
      if (padRumbleEnabled()) pad.appendChild(sliderRow('Rumble strength', 0, 1, 0.05, padRumbleGain(), (v) => `${Math.round(v * 100)}%`, (v) => { setPadRumbleGain(v); padRumble('hit'); }));

      const head = el('div', 'op-bind-head');
      head.appendChild(el('div', 'op-note', padCaptureActive() ? 'Press a button on the controller. Esc cancels.' : 'Choose a button, then press the new one on the controller.'));
      const reset = btn('Reset gamepad', () => { resetPadBindings(); padCancelCapture(); this.render(); }, 'op-btn op-btn-small');
      reset.dataset.k = 'pad:reset';
      head.appendChild(reset);
      pad.appendChild(head);
      const live = padBindings();
      const seenAction = new Set<string>();
      for (const [ctx, actions] of Object.entries(PAD_CONTEXTS)) {
        pad.appendChild(el('div', 'op-bind-ctx', contextLabel(ctx)));
        for (const a of actions) {
          if (seenAction.has(a)) continue;           // `menu` lives in every context
          seenAction.add(a);
          const row = el('div', 'op-bind');
          row.dataset.action = a;
          const capturing = padCaptureActive() === a;
          const set = btn(capturing ? 'Press a button' : live[a].map((i) => PAD_BUTTON_LABEL[i] ?? `#${i}`).join(' / '),
            () => { if (padCaptureActive() === a) padCancelCapture(); else padBeginCapture(a); this.render(); }, `op-btn op-btn-small op-bind-set${capturing ? ' is-capturing' : ''}`);
          set.dataset.k = `pad:${a}`;
          row.append(el('div', 'op-bind-name', actionLabel(a)), set);
          pad.appendChild(row);
        }
      }
      // The outcome of the last rebind: a refusal names the action it would have collided with, because a button
      // meaning two things in one context is the one way this scheme breaks.
      const last = padLastCapture();
      if (last) {
        const bad = last.conflicts.length > 0;
        pad.appendChild(el('div', `op-result${bad ? ' is-bad' : ''}`, bad
          ? `${actionLabel(last.action)} cannot take that button: ${last.conflicts.map(actionLabel).join(', ')} already ${last.conflicts.length === 1 ? 'uses' : 'use'} it in the same situation.`
          : `${actionLabel(last.action)} is now ${PAD_BUTTON_LABEL[last.button ?? -1] ?? 'unbound'}.`));
      }
    }
    cols.append(kb, pad);
    body.appendChild(cols);

    // The sticks and triggers are not rebindable (they are axes, not buttons): say what they do.
    body.appendChild(el('div', 'op-group', 'Sticks and triggers'));
    const help = el('div', 'op-help');
    for (const [where, what] of [
      ['On foot', `Left stick moves, right stick looks. ${padBindingText('jump')} jump, ${padBindingText('crouch')} crouch, ${padBindingText('sprint')} sprint, ${padBindingText('attack')} attack, ${padBindingText('guard')} guard, ${padBindingText('interact')} interact, ${padBindingText('weaponNext')} next weapon. D-pad up and down move the camera in and out.`],
      ['Hovercraft', 'Right trigger is the throttle, left trigger brakes and reverses, the left stick steers. RB boosts, LB is the handbrake.'],
      ['Flying', `Left stick is throttle and roll, right stick points the nose. ${padBindingText('fire')} fire, ${padBindingText('boost')} afterburner, ${padBindingText('levelOut')} level out.`],
      ['Menus', `D-pad or left stick moves, the right stick scrolls. ${padBindingText('uiAccept')} select, ${padBindingText('uiBack')} close, ${padBindingText('uiTabPrev')} and ${padBindingText('uiTabNext')} change tab.`],
      ['Anywhere', `${padBindingText('menu')} opens this menu, ${padBindingText('map')} the map, ${padBindingText('inventory')} the inventory.`],
    ] as const) {
      const line = el('div', 'op-help-line');
      line.append(el('span', 'op-help-where', where), el('span', '', what));
      help.appendChild(line);
    }
    body.appendChild(help);

    // ── Touch controls ── (arrange, 2026-09-16)
    if (matchMedia('(pointer: coarse)').matches || localStorage.getItem('atlas:touch-ui') === 'on') {
      body.appendChild(el('div', 'op-group', 'Touch controls'));
      const input = this.app?.getModule?.('AtlasInput') as { setTouchArrange?: (b: boolean) => void; touchArrangeOn?: () => boolean; touchControls?: () => readonly { id: string; action: string }[]; addTouchControl?: (a: string) => void; removeTouchControl?: (id: string) => void; resetTouchControls?: () => void } | null;
      body.appendChild(toggleRow('Arrange controls', input?.touchArrangeOn?.() ?? false, (v) => { input?.setTouchArrange?.(v); this.render(); }, 'drag to move, drag a corner to resize'));
      for (const c of input?.touchControls?.() ?? []) {
        const row = el('div', 'op-bind');
        const rm = btn('Remove', () => { input?.removeTouchControl?.(c.id); this.render(); }, 'op-btn op-btn-small');
        if (c.action === 'stick') rm.disabled = true;     // without it there is no walking
        row.append(el('div', 'op-bind-name', TOUCH_LABEL[c.action as keyof typeof TOUCH_LABEL]?.name ?? c.action), rm);
        body.appendChild(row);
      }
      const addRow = el('div', 'op-chips');
      for (const a3 of Object.keys(TOUCH_LABEL)) {
        if (a3 === 'stick') continue;
        addRow.appendChild(btn(`Add ${TOUCH_LABEL[a3 as keyof typeof TOUCH_LABEL].name}`, () => { input?.addTouchControl?.(a3); this.render(); }, 'op-btn op-btn-small'));
      }
      addRow.appendChild(btn('Reset touch layout', () => { input?.resetTouchControls?.(); this.render(); }, 'op-btn op-btn-small'));
      body.appendChild(addRow);
    }
  }

  // ── General ── who you are, where you are, what is drawing the picture
  private infoFacts(): Array<[string, Array<[string, string, string?]>]> {
    const n = this.app.renderer.native as unknown as { backend?: { constructor?: { name?: string } }; getDrawingBufferSize(v: THREE.Vector2): THREE.Vector2; getPixelRatio(): number };
    let canvas = 'not ready';
    try { const v = n.getDrawingBufferSize(new THREE.Vector2()); canvas = `${v.x} × ${v.y}, pixel ratio ${n.getPixelRatio().toFixed(2)}`; } catch { /* no renderer yet */ }
    const backend = n.backend?.constructor?.name === 'WebGPUBackend' ? 'WebGPU' : 'WebGL 2';
    const world = atlas.spaceFlight ? 'Space' : atlas.currentWorldId === null ? 'Not in a world yet'
      : atlas.currentWorldId === atlas.commonsWorldId ? 'The Commons'
        : atlas.currentWorldId === atlas.personalWorldId ? 'Your personal world' : `World ${atlas.currentWorldId}`;
    const conn = atlas.conn as unknown as { isActive?: boolean } | null;
    const pending = draftChanges().length;
    const kb = keyBindings() as unknown as Record<string, string[]>;
    const key = (a: string): string => (kb[a]?.length ? keyLabel(kb[a][0]) : 'unbound');   // read from the LIVE bindings: a rebind shows here
    return [
      ['Traveler', [
        ['Name', atlas.displayName ?? 'No traveler yet'],
        ['Handle', atlas.username ? `@${atlas.username}` : 'None'],
        ['Identity', atlas.identityHex ? `${atlas.identityHex.slice(0, 16)}…` : 'Not signed in'],
      ]],
      ['Where you are', [
        ['World', world],
        ['Position', atlas.currentWorldId === null ? 'None' : `${Math.round(atlas.localX)}, ${Math.round(atlas.localZ)} at ${Math.round(atlas.localY)} m`, 'pos'],
        ['Server', conn ? (conn.isActive === false ? 'Disconnected' : 'Connected') : 'Offline'],
      ]],
      ['Keys at a glance', [
        ['Move', `${key('moveForward')} ${key('moveLeft')} ${key('moveBack')} ${key('moveRight')}`],
        ['Jump, sprint', `${key('jump')}, ${key('sprint')}`],
        ['Crouch', key('crouch')],
        ['Interact', key('interact')],
        ['Attack', `Left click with the mouse captured${kb.attack?.length ? `, or ${key('attack')}` : ''}`],
        ['Guard', 'Hold the right mouse button'],
        ['Map, inventory', `${key('map')}, ${key('inventory')}`],
      ]],
      ['Picture', [
        ['Renderer', backend],
        ['Canvas', canvas],
        ['Graphics preset', `${TIER_LABEL[activeTier()]}${isCustom() ? ', customised' : ''}${pending ? ' (changes waiting on the Graphics tab)' : ''}`],
        ['Frame rate', this.fpsText || 'Measuring', 'fps'],
      ]],
    ];
  }
  private renderGeneral(): void {
    const body = this.body!;
    const cols = el('div', 'op-cols');
    const facts = this.infoFacts();
    const colA = el('div'), colB = el('div');
    facts.forEach(([title, rows], i) => {
      const host = i < 3 ? colA : colB;
      host.appendChild(el('div', 'op-group', title));
      for (const [k, v, liveKey] of rows) {
        const row = el('div', 'op-fact');
        const val = el('div', 'op-fact-v', v);
        if (liveKey) this.liveEls[liveKey] = val;
        row.append(el('div', 'op-fact-k', k), val);
        host.appendChild(row);
      }
    });
    colB.appendChild(el('div', 'op-group', 'This menu'));
    colB.appendChild(noteRow('Graphics changes wait for the Apply changes button, so any number of them cost one rebuild of the picture. Sound and controls change as you set them.'));
    colB.appendChild(noteRow('Esc closes the menu. With the mouse captured, the first Esc frees the mouse and the second opens the menu.'));
    const copy = btn('Copy this information', () => {
      const text = this.infoFacts().map(([t, rows]) => `${t}\n${rows.map(([k, v]) => `  ${k}: ${v}`).join('\n')}`).join('\n');
      const done = (msg: string): void => { copy.textContent = msg; window.setTimeout(() => { copy.textContent = 'Copy this information'; }, 1800); };
      try { void navigator.clipboard.writeText(text).then(() => done('Copied'), () => done('Copying was blocked by the browser')); } catch { done('Copying was blocked by the browser'); }
    }, 'op-btn');
    copy.dataset.k = 'general:copy';
    copy.title = 'For a bug report';
    colB.appendChild(copy);
    cols.append(colA, colB);
    body.appendChild(cols);
  }

  private currentFov(): number {
    try { return (this.app.renderer.cameras.main as unknown as { fov?: number }).fov ?? 70; } catch { return 70; }
  }
  /** Apply the persisted FOV to the live camera (boot + slider). */
  private applyFov(): void {
    const deg = activePreset().cameraFov ?? 0;
    if (deg <= 0) return;
    try {
      const cam = this.app.renderer.cameras.main as unknown as { fov: number; updateProjectionMatrix(): void };
      cam.fov = deg;
      cam.updateProjectionMatrix();
    } catch { /* no perspective camera yet */ }
  }

  // ── Auto-tune: walk the preset ladder to hold playable frame rates.
  // Down at <42 fps sustained 3 s (20 s cooldown); up at >57 fps sustained
  // 60 s. Custom settings are respected — the tuner never discards them.
  private tuneBelow = 0;
  private tuneAbove = 0;
  private tuneCooldownMs = 0;

  private updateFps(dtSeconds: number): void {
    this.fpsAccumMs += dtSeconds * 1000;
    this.fpsFrames++;
    if (this.tuneCooldownMs > 0) this.tuneCooldownMs -= dtSeconds * 1000;
    if (this.fpsAccumMs >= 500) {
      const avgMs = this.fpsAccumMs / this.fpsFrames;
      const fps = 1000 / avgMs;
      this.fpsText = `${Math.round(fps)} fps, ${avgMs.toFixed(1)} ms a frame`;
      if (this.open && this.fpsEl) {
        const [a, b] = Array.from(this.fpsEl.children) as HTMLElement[];
        if (a) a.textContent = `${Math.round(fps)} fps`;
        if (b) b.textContent = `${avgMs.toFixed(1)} ms`;
        if (this.liveEls.fps) this.liveEls.fps.textContent = this.fpsText;
        if (this.liveEls.pos && atlas.currentWorldId !== null) this.liveEls.pos.textContent = `${Math.round(atlas.localX)}, ${Math.round(atlas.localZ)} at ${Math.round(atlas.localY)} m`;
      }
      this.fpsAccumMs = 0;
      this.fpsFrames = 0;
      if ((activePreset().autoTune ?? false) && !isCustom() && this.tuneCooldownMs <= 0 && !document.hidden) {
        this.tuneBelow = fps < 42 ? this.tuneBelow + 1 : 0;
        this.tuneAbove = fps > 57 ? this.tuneAbove + 1 : 0;
        const idx = TIER_ORDER.indexOf(activeTier());
        if (this.tuneBelow >= 6 && idx > 0) {
          this.postMod()?.setTier(TIER_ORDER[idx - 1]);
          setAutoTune(true); // setTier resets to the preset — keep the tuner armed
          this.tuneBelow = 0; this.tuneAbove = 0; this.tuneCooldownMs = 20_000;
          if (this.open) this.render();
        } else if (this.tuneAbove >= 120 && idx < TIER_ORDER.length - 1) {
          this.postMod()?.setTier(TIER_ORDER[idx + 1]);
          setAutoTune(true);
          this.tuneBelow = 0; this.tuneAbove = 0; this.tuneCooldownMs = 30_000;
          if (this.open) this.render();
        }
      }
    }
  }

  dispose(): void {
    if (this.keyHandler) { window.removeEventListener('keydown', this.keyHandler, true); this.keyHandler = null; }
    this.overlay?.remove();
    this.overlay = null;
    this.body = null; this.tabsEl = null; this.railEl = null; this.footEl = null;
    this.fpsEl = null;
    document.getElementById('atlas-options-style')?.remove();
  }
}

// ─── DOM helpers ──────────────────────────────────────────────────────────────

type Tab = 'general' | 'graphics' | 'sound' | 'controls';
const TABS: ReadonlyArray<[Tab, string]> = [['general', 'General'], ['graphics', 'Graphics'], ['sound', 'Sound'], ['controls', 'Controls']];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

function btn(label: string, onClick: () => void, cls = ''): HTMLButtonElement {
  const b = el('button', cls, label);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

/** 'weaponNext' → 'Weapon next'; 'uiAccept' → 'Menu accept' — the binding tables name actions for code, not for people. */
const ACTION_LABEL: Record<string, string> = {
  weaponNext: 'Next weapon', weaponPrev: 'Previous weapon', zoomIn: 'Camera closer', zoomOut: 'Camera farther',
  uiAccept: 'Menu select', uiBack: 'Menu back', uiTabNext: 'Menu next tab', uiTabPrev: 'Menu previous tab',
  editToolNext: 'Edit next tool', editToolPrev: 'Edit previous tool', throttleUp: 'Throttle up', throttleDown: 'Throttle down',
};
function actionLabel(a: string): string {
  if (ACTION_LABEL[a]) return ACTION_LABEL[a];
  const words = a.replace(/^ui(?=[A-Z])/, 'menu ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
const CONTEXT_LABEL: Record<string, string> = { foot: 'On foot', vehicle: 'Hovercraft', flight: 'Flying', ui: 'Menus', edit: 'Edit mode' };
function contextLabel(ctx: string): string { return CONTEXT_LABEL[ctx] ?? actionLabel(ctx); }

/** A row: the label (with an optional quiet note) on the left, the control cell on the right. */
function labeledRow(label: string, when = '', was = ''): HTMLDivElement {
  const row = el('div', 'op-row');
  const l = el('div', 'op-label', label);
  if (when) l.appendChild(el('span', 'op-when', when));
  const ctl = el('div', 'op-ctl');
  if (was) ctl.appendChild(el('span', 'op-was', was));
  row.append(l, ctl);
  return row;
}

/** A label + a row of segmented option buttons; the current option is filled. */
function segRow(label: string, values: readonly string[], labels: readonly string[], current: string, onPick: (v: string) => void, when = '', was = ''): HTMLDivElement {
  const row = labeledRow(label, when, was);
  const seg = el('div', 'op-seg');
  seg.setAttribute('role', 'group');
  seg.setAttribute('aria-label', label);
  values.forEach((v, i) => {
    const b = btn(labels[i], () => onPick(v));
    b.dataset.k = `${label}|${v}`;        // the re-render hands the focus back to this control
    b.setAttribute('aria-pressed', String(v === current));
    if (v === current) b.classList.add('on');
    seg.appendChild(b);
  });
  row.lastElementChild!.appendChild(seg);
  return row;
}

function toggleRow(label: string, on: boolean, onChange: (on: boolean) => void, when = ''): HTMLDivElement {
  return segRow(label, ['off', 'on'], ['Off', 'On'], on ? 'on' : 'off', (v) => onChange(v === 'on'), when);
}

/** A label + a slider + a live value readout; fires onChange on release (not every tick). */
function sliderRow(label: string, min: number, max: number, step: number, value: number, fmt: (v: number) => string, onChange: (v: number) => void, live = false, when = '', was = ''): HTMLDivElement {
  const row = labeledRow(label, when, was);
  const slider = el('input', 'op-range');
  slider.type = 'range';
  slider.min = String(min); slider.max = String(max); slider.step = String(step); slider.value = String(value);
  slider.dataset.k = `${label}|range`;
  slider.setAttribute('aria-label', label);
  const val = el('span', 'op-val', fmt(value));
  // 'input' updates the readout while dragging; 'change' (release) commits — staging a graphics row per tick would
  // re-render the list under the pointer. `live` (the audio volumes) applies while dragging too: a level is set by ear.
  slider.addEventListener('input', () => { const v = parseFloat(slider.value); val.textContent = fmt(v); if (live) onChange(v); });
  slider.addEventListener('change', () => onChange(parseFloat(slider.value)));
  row.lastElementChild!.append(slider, val);
  return row;
}

/** A quiet explanatory line under a control. */
function noteRow(text: string): HTMLDivElement { return el('div', 'op-note', text); }

// The Esc menu wears the game's own slate (the loading screen, the traveler screen): burnt-umber ink, a brass seam,
// the stencil wordmark face for the title, Sora for reading, Plex Mono for the numbers only. The ONE loud element is
// the Apply bar — a quiet line when there is nothing to apply, a solid brass strip when changes are waiting.
const OPTIONS_CSS = `
#atlas-options{position:fixed;inset:0;display:none;align-items:center;justify-content:center;z-index:200;background:rgba(9,6,4,.64);
  --ink:#120d0a;--ink2:#1b1510;--ink3:#2a2019;--line:#3a2e24;--bone:#efe6d2;--soft:#d3c8b1;--dust:#8c7a66;--brass:#d9a441;--rust:#c9573a;--cyan:#5fa8c9;
  font:400 14px/1.45 Sora,'Segoe UI',system-ui,sans-serif;color:var(--bone)}
#atlas-options *{box-sizing:border-box}
#atlas-options .op-panel{width:min(760px,96vw);height:min(86vh,820px);display:flex;flex-direction:column;background-color:var(--ink);
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 .92  0 0 0 0 .8  0 0 0 .07 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
  border:1px solid var(--line);border-left:2px solid var(--brass);box-shadow:0 22px 70px rgba(0,0,0,.7)}
#atlas-options .op-head{display:flex;align-items:center;gap:16px;padding:16px 20px 2px 22px}
#atlas-options .op-title{font:700 32px/1 'Big Shoulders Stencil Display','Arial Narrow',Impact,sans-serif;letter-spacing:.03em;color:var(--brass)}
#atlas-options .op-fps{margin-left:auto;display:flex;gap:14px;font:500 12px/1 'IBM Plex Mono',ui-monospace,monospace;color:var(--dust);font-variant-numeric:tabular-nums}
#atlas-options button{font:inherit;color:inherit;cursor:pointer}
#atlas-options button:disabled{cursor:default}
#atlas-options :focus-visible{outline:2px solid var(--cyan);outline-offset:2px}
#atlas-options .op-btn{background:var(--ink2);border:1px solid #4a3b2e;border-radius:3px;padding:6px 13px;font-size:13px;font-weight:500;color:var(--bone)}
#atlas-options .op-btn:hover:not(:disabled){border-color:var(--dust);background:var(--ink3)}
#atlas-options .op-btn:disabled{opacity:.4}
#atlas-options .op-btn-small{padding:4px 10px;font-size:12.5px}
#atlas-options .op-tabs{display:flex;gap:4px;padding:10px 20px 0 18px;border-bottom:1px solid var(--line)}
#atlas-options .op-tab{background:none;border:0;border-bottom:3px solid transparent;margin-bottom:-1px;padding:9px 14px 9px;font-size:15px;font-weight:600;color:var(--dust)}
#atlas-options .op-tab:hover{color:var(--soft)}
#atlas-options .op-tab[aria-selected="true"]{color:var(--bone);border-bottom-color:var(--brass)}
#atlas-options .op-tab[data-dirty]::after,#atlas-options .op-rail-link[data-dirty]::after{content:'';display:inline-block;flex:none;width:7px;height:7px;border-radius:50%;background:var(--rust);margin-left:8px;vertical-align:1px}
#atlas-options .op-rail-link[data-dirty]::after{margin:0 10px 0 0}
#atlas-options .op-main{flex:1;min-height:0;display:flex}
#atlas-options .op-rail{flex:none;width:192px;padding:12px 0 12px 10px;border-right:1px solid #2e241c;overflow-y:auto;scrollbar-width:none}
#atlas-options .op-rail-link{display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;text-align:left;white-space:nowrap;background:none;border:0;border-left:2px solid transparent;padding:6px 8px 6px 12px;font-size:13px;color:var(--dust)}
#atlas-options .op-rail-link:hover{color:var(--soft)}
#atlas-options .op-rail-link.is-here{color:var(--bone);border-left-color:var(--brass)}
#atlas-options .op-body{flex:1;min-width:0;overflow-y:auto;padding:2px 22px 20px 24px;scrollbar-width:thin;scrollbar-color:#4a3b2e transparent}
#atlas-options .op-group{margin:18px 0 6px;padding-bottom:6px;border-bottom:1px solid #2e241c;font-size:13px;font-weight:600;color:var(--brass)}
#atlas-options .op-row{display:flex;align-items:center;justify-content:space-between;gap:14px;min-height:34px;padding:3px 0 3px 10px;margin-left:-12px;border-left:2px solid transparent}
#atlas-options .op-row.is-changed{border-left-color:var(--rust)}
#atlas-options .op-row-wide{justify-content:flex-start;flex-wrap:wrap}
#atlas-options .op-label{min-width:0;color:var(--soft);font-size:13.5px}
#atlas-options .op-when{margin-left:9px;font-size:12px;color:var(--dust)}
#atlas-options .op-ctl{flex:none;display:flex;align-items:center;gap:10px}
#atlas-options .op-was{font-size:12px;color:#e39a82;white-space:nowrap}
#atlas-options .op-seg{display:inline-flex;border:1px solid #4a3b2e;border-radius:3px;overflow:hidden;background:var(--ink2)}
#atlas-options .op-seg button{background:none;border:0;border-right:1px solid #4a3b2e;padding:4px 10px;font-size:12.5px;font-weight:500;color:var(--soft);white-space:nowrap}
#atlas-options .op-seg button:last-child{border-right:0}
#atlas-options .op-seg button:hover{background:var(--ink3)}
#atlas-options .op-seg button.on{background:var(--brass);color:#1a1209;font-weight:600}
#atlas-options .op-seg button.base{box-shadow:inset 0 -2px 0 var(--brass);color:var(--bone)}
#atlas-options .op-seg button:focus-visible{outline-offset:-2px}
#atlas-options .op-range{width:190px;accent-color:var(--brass)}
#atlas-options .op-val{min-width:60px;text-align:right;font:500 12px/1 'IBM Plex Mono',ui-monospace,monospace;color:var(--bone);font-variant-numeric:tabular-nums}
#atlas-options .op-note{margin:2px 0 8px;font-size:12.5px;line-height:1.5;color:var(--dust);max-width:64ch}
#atlas-options .op-cols{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:0 30px;align-items:start}
#atlas-options .op-cols .op-range{width:120px}
#atlas-options .op-cols .op-when{display:block;margin:1px 0 0}
#atlas-options .op-fact{display:flex;gap:12px;padding:5px 0;font-size:13.5px}
#atlas-options .op-fact-k{flex:none;width:118px;color:var(--dust)}
#atlas-options .op-fact-v{min-width:0;color:var(--bone);overflow-wrap:anywhere}
#atlas-options .op-bind-head{display:flex;align-items:flex-start;gap:10px;margin:2px 0 4px}
#atlas-options .op-bind-head .op-note{flex:1;margin:0}
#atlas-options .op-bind-ctx{margin:12px 0 2px;font-size:12.5px;font-weight:600;color:var(--soft)}
#atlas-options .op-bind{display:flex;align-items:center;gap:6px;padding:2px 0}
#atlas-options .op-bind-name{flex:1;min-width:0;font-size:13px;color:var(--soft)}
#atlas-options .op-bind-set{min-width:92px;text-align:center}
#atlas-options .op-bind-set.is-capturing{border-color:var(--brass);color:var(--brass)}
#atlas-options .op-bind-clear{background:none;border-color:transparent;color:var(--dust);padding:4px 6px}
#atlas-options .op-bind-clear:hover:not(:disabled){background:none;border-color:transparent;color:var(--bone);text-decoration:underline}
#atlas-options .op-bind-clear:disabled{visibility:hidden}
#atlas-options .op-result{margin-top:10px;font-size:12.5px;color:#a9c9a4}
#atlas-options .op-result.is-bad{color:#e39a82}
#atlas-options .op-help{font-size:13px;color:var(--soft)}
#atlas-options .op-help-line{display:flex;gap:12px;padding:4px 0;line-height:1.5}
#atlas-options .op-help-where{flex:none;width:96px;color:var(--dust)}
#atlas-options .op-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
#atlas-options .op-apply{display:flex;align-items:center;gap:10px;padding:12px 20px 12px 22px;border-top:1px solid var(--line);background:var(--ink2);color:var(--dust)}
#atlas-options .op-apply-text{flex:1;min-width:0;font-size:13.5px;font-weight:600}
#atlas-options .op-apply.is-pending{background:var(--brass);border-top-color:var(--brass);color:#1a1209}
#atlas-options .op-apply.is-pending .op-apply-go{background:#1a1209;border-color:#1a1209;color:var(--bone);padding:8px 20px;font-weight:600}
#atlas-options .op-apply.is-pending .op-apply-go:hover{background:#2a1d10}
#atlas-options .op-apply.is-pending .op-apply-discard{background:transparent;border-color:rgba(26,18,9,.55);color:#1a1209}
#atlas-options .op-apply.is-pending .op-apply-discard:hover{background:rgba(26,18,9,.12)}
#atlas-options .op-apply.is-pending :focus-visible{outline-color:#1a1209}
@media (prefers-reduced-motion:no-preference){#atlas-options .op-apply{transition:background-color .18s ease,color .18s ease}}
@media (max-width:720px){
  #atlas-options .op-panel{height:94vh}
  #atlas-options .op-main{flex-direction:column}
  #atlas-options .op-rail{width:auto;display:flex;gap:2px;overflow-x:auto;padding:6px 12px;border-right:0;border-bottom:1px solid #2e241c}
  #atlas-options .op-rail-link{width:auto;white-space:nowrap;border-left:0;border-bottom:2px solid transparent;padding:5px 9px}
  #atlas-options .op-rail-link.is-here{border-bottom-color:var(--brass)}
  #atlas-options .op-body{padding:2px 14px 18px 18px}
  #atlas-options .op-row{flex-wrap:wrap;gap:4px 14px}
  #atlas-options .op-cols{grid-template-columns:minmax(0,1fr)}
  #atlas-options .op-fps{display:none}
  #atlas-options .op-tab{padding:9px 10px;font-size:14px}
}
`;
